/**
 * RUN 96 - the Report Studio's push, through the TMS Push extension.
 *
 * Authority: the TD's instruction of 27 September 2026 (push every official's report to TMS as a
 * draft - TOs, Judges, ATDs, UMs and umpires); `09-tms-write-paths.md` sections 3 and 5.1 (the
 * flow, the receipt, every failure branch said plainly); `12-tms-push-extension.md` sections 4-6
 * (the page asks, the extension shows the plan in its own window, posts only on the TD's Confirm
 * there, and reads it back) - the appointments push's flow, one press at a time.
 *
 *   hello (3 s)  ->  POST .../report/push/prepare { pressId }  ->  push-report over a port
 *                ->  POST .../report/push/report { pressId, result }  ->  ack
 *
 * THE HONEST STATES, the appointments push's:
 *  * no extension answers: NOTHING is prepared and nothing is recorded - the page says what to
 *    check, and Copy For TMS and Download Word stay where they are;
 *  * the extension refuses the plan (busy, a plan it will not take): nothing was sent, in its words;
 *  * the port is lost, or its message cannot be read: the page asks the extension what it KEPT for
 *    the press; with nothing to go on it says the report may or may not have been saved - check TMS;
 *  * a result: reported to the API exactly as the extension sent it, and the API decides - the
 *    press's two receipts, and `TMS Draft` only when the read-back is the plan.
 *
 * A prepare or report whose answer was lost is asked ONCE more with the same press id (the API
 * answers a known press from its record), and never more: a press is never repeated.
 */

import type { TmsReportPushPlan, TmsReportPushResultMessage } from "@hockeytd/shared";

import { ApiError, prepareReportPush, reportReportPush } from "../../api/client.js";
import type { PushReceipt, ReportPushBody, ReportPushPrepareBody, ReportPushReportBody } from "../../api/types.js";
import {
  LIVE_TMS_PUSH_BRIDGE,
  TMS_PUSH_PRESS_HELLO_TIMEOUT_MS,
  type TmsPushBridge,
  type TmsPushStage,
  type TmsReportPushPortEnding,
} from "../appointments/tmsPushExtension.js";

/** What the page says while the press is out, stage by stage. */
export const REPORT_PUSH_STAGE_WORDS: Readonly<Record<TmsPushStage, string>> = {
  "awaiting-confirm": "The TMS Push window has opened: check the marks and the report there, then Confirm - or Cancel.",
  reading: "The TMS Push extension is reading the official's marking from TMS…",
  posting: "The TMS Push extension is saving the report in TMS - one save, as a draft…",
  verifying: "The TMS Push extension is reading the save back from TMS…",
};

/** No extension answered: nothing was prepared, recorded or sent. */
export const EXTENSION_ABSENT_TEXT =
  "The TMS Push extension did not answer in this browser, so nothing was sent to TMS and nothing was recorded. " +
  "Check that hockeytd TMS Push (0.3.0 or later) is installed and switched on in Chrome's extensions page, and that you are signed in to TMS in this Chrome - " +
  "or use Copy For TMS or Download Word and put the report into TMS yourself.";

/** A press that ended with no result this page can believe. */
export function outcomeUnknownText(pressId: string): string {
  return (
    "The TMS Push window's outcome did not reach this page, so it is not known whether TMS saved the report. " +
    `Check the official's marking in TMS by eye before pressing again (press ${pressId}).`
  );
}

/**
 * A push that did not land. `recorded` is the API's answer when the press WAS recorded (a failed
 * receipt pair) - the screen then shows the stored receipt, never a made-up one.
 */
export class ReportPushNotLanded extends Error {
  public readonly recorded: ReportPushBody | null;
  public constructor(message: string, recorded: ReportPushBody | null = null) {
    super(message);
    this.name = "ReportPushNotLanded";
    this.recorded = recorded;
  }
}

export interface ReportPushFlowDeps {
  readonly bridge: TmsPushBridge;
  readonly prepare: (tmsPersonId: string, pressId: string) => Promise<ReportPushPrepareBody>;
  readonly report: (tmsPersonId: string, pressId: string, result: TmsReportPushResultMessage) => Promise<ReportPushReportBody>;
  readonly newPressId: () => string;
}

const LIVE_DEPS: ReportPushFlowDeps = {
  bridge: LIVE_TMS_PUSH_BRIDGE,
  prepare: prepareReportPush,
  report: reportReportPush,
  newPressId: () => crypto.randomUUID(),
};

/** A lost answer (no answer, or a 5xx) - worth asking once more with the same press id. */
function lostAnswer(error: unknown): boolean {
  return error instanceof ApiError && (error.status === 0 || error.status >= 500);
}

async function onceMore<T>(ask: () => Promise<T>): Promise<T> {
  try {
    return await ask();
  } catch (error) {
    if (!lostAnswer(error)) throw error;
    return ask();
  }
}

/** The receipt a report push shows: the report half (the marks half says the same). */
function shownReceipt(body: ReportPushReportBody): PushReceipt {
  const found = body.receipts.find((receipt) => receipt.kind === "report") ?? body.receipts[0];
  if (found === undefined) throw new ReportPushNotLanded("The Control Centre recorded the press but returned no receipt. Check the official's marking in TMS by eye.");
  return found;
}

async function resultOf(ending: TmsReportPushPortEnding, bridge: TmsPushBridge, pressId: string): Promise<TmsReportPushResultMessage> {
  switch (ending.kind) {
    case "result":
      return ending.result;
    case "unavailable":
      throw new ReportPushNotLanded(EXTENSION_ABSENT_TEXT);
    case "busy":
      throw new ReportPushNotLanded("Another push from this page is still out in the TMS Push extension. Finish or cancel it there first. Nothing was sent to TMS for this one.");
    case "stopped":
      throw new ReportPushNotLanded(outcomeUnknownText(pressId));
    case "refused":
      if (ending.reason === "busy" || ending.reason === "invalid-message") {
        throw new ReportPushNotLanded(`The TMS Push extension would not take this push: ${ending.detail}`);
      }
      break;
    case "lost":
    case "unreadable":
      break;
  }
  // The extension may have run the press: ask what it kept.
  const status = bridge.reportStatus === undefined ? null : await bridge.reportStatus(pressId);
  if (status?.kind === "result") return status.result;
  throw new ReportPushNotLanded(outcomeUnknownText(pressId));
}

/**
 * The Report Studio's `push` - one human press, through the extension. Resolves with the stored
 * report and the report receipt when TMS accepted the draft AND the API believed the read-back;
 * throws {@link ReportPushNotLanded} (or the API's own `ApiError`) otherwise.
 */
export function createExtensionReportPusher(deps: ReportPushFlowDeps = LIVE_DEPS) {
  return async function pushThroughExtension(tmsPersonId: string, onProgress: (words: string) => void = () => undefined): Promise<ReportPushBody> {
    const { bridge } = deps;
    if (bridge.pushReport === undefined) throw new ReportPushNotLanded(EXTENSION_ABSENT_TEXT);
    const presence = await bridge.hello(TMS_PUSH_PRESS_HELLO_TIMEOUT_MS);
    if (presence.kind === "absent") throw new ReportPushNotLanded(EXTENSION_ABSENT_TEXT);

    const pressId = deps.newPressId();
    onProgress("Preparing the push in the Control Centre…");
    const prepared = await onceMore(() => deps.prepare(tmsPersonId, pressId));
    const plan: TmsReportPushPlan = prepared.plan;

    onProgress(REPORT_PUSH_STAGE_WORDS["awaiting-confirm"]);
    const ending = await bridge.pushReport(plan, (stage) => {
      onProgress(REPORT_PUSH_STAGE_WORDS[stage]);
    });
    const result = await resultOf(ending, bridge, pressId);

    onProgress("Recording what TMS saved…");
    const body = await onceMore(() => deps.report(tmsPersonId, pressId, result));
    // Only now may the extension forget its copy: the API has the result.
    void bridge.ack(pressId).catch(() => undefined);
    const receipt = shownReceipt(body);
    const recorded = { report: body.report, receipt };
    if (body.outcome !== "accepted_draft") throw new ReportPushNotLanded(receipt.detail, recorded);
    if (body.stageRefusal !== null) {
      throw new ReportPushNotLanded(`TMS holds the draft, and the receipt says so - but the report's stage here did not move: ${body.stageRefusal}`, recorded);
    }
    return recorded;
  };
}

/** The live pusher, for the Report Studio. */
export const pushReportThroughExtension = createExtensionReportPusher();

/** Whether the extension answers at all - for the note beside the button (one second, as the day push's). */
export async function reportPushExtensionPresent(bridge: TmsPushBridge = LIVE_TMS_PUSH_BRIDGE): Promise<boolean> {
  if (bridge.pushReport === undefined) return false;
  return (await bridge.hello()).kind === "present";
}
